import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { PERSON_TYPES, isPlaceholderEntityName } from "score-common";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveOrg, useActiveShow, userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useOrgMembers } from "../hooks/useOrgMembers";
import { useSortedRows } from "../hooks/useSortedRows";
import { useNextRides } from "../hooks/useNextRides";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import NextRideCell from "../components/NextRideCell";
import RideHistoryDrawer from "../components/RideHistoryDrawer";
import ScoresheetRecipientsEditor, { staffRecipientsFor } from "../components/ScoresheetRecipientsEditor";
import { splitEmailAddresses } from "../lib/splitEmailAddresses";

// #507: replace the People placeholder with a real search + detail screen.
// #592: FVD types nearly everyone as personType=RIDER (owners/trainers/
// coaches included), and judges/scribes aren't score-persons at all — judges
// live as NAMES on class.judgeAssignments, scribes are account members with
// the scribe role. So each tab now has its own data source instead of all
// three sharing listPeople(personType):
//   - Riders: GET /show/:showId/riders (#3184), the show's distinct RIDER
//     person rows joined server-side (thin ride GSI + person BatchGet). It
//     used to be listPeople(RIDER) x every ride of the show, joined here.
//   - Judges: distinct names collected from every class's judgeAssignments.
//   - Scribes: org members holding the score:score_scribe role (mirrors
//     ClassManager.jsx's scribe load).
// Only the Riders tab keeps the rich sortable table + RideHistoryDrawer;
// Judges/Scribes render a simple searchable name list.
const SCRIBE_ROLE = "score:score_scribe";

const TABS = [
  { value: PERSON_TYPES.RIDER, labelKey: "people.tabs.riders" },
  { value: PERSON_TYPES.JUDGE, labelKey: "people.tabs.judges" },
  { value: PERSON_TYPES.SCRIBE, labelKey: "people.tabs.scribes" },
];

function personName(person) {
  return [person.firstName, person.lastName].filter(Boolean).join(" ") || person.id;
}

export default function PeopleManager() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.people"));
  const scoreApi = useScoreApi();
  const { activeOrgId } = useActiveOrg();
  const [personType, setPersonType] = useState(PERSON_TYPES.RIDER);
  // #2339 — paged org-members fetch (follows nextCursor to the end), shared
  // with ClassManager's scribe load and useOrgMembersById. Only pass a real
  // orgId while the Scribes tab is active (personType===SCRIBE) -- the org
  // member endpoint has nothing to do with the Riders/Judges tabs, and
  // gating it this way preserves the original "fetch on tab view, not on
  // page mount" behavior rather than issuing it unconditionally on every
  // People page visit.
  const { members: orgMembers, loading: scribesLoading, error: scribesError } = useOrgMembers(
    personType === PERSON_TYPES.SCRIBE ? activeOrgId : null,
  );
  const { activeShowId, loading: showsLoading } = useActiveShow();
  // #558 — each rider row shows their next ride in the active show.
  const { nextByRider } = useNextRides(activeShowId);

  const [people, setPeople] = useState([]); // riders: the active show's rider person rows
  const [judgeNames, setJudgeNames] = useState([]); // judges: distinct names off classes
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Riders: one call, GET /show/:showId/riders (#3184). The skeleton stays up
  // until THAT call settles, never on a partial answer: "No people found."
  // used to flash because the people list landed before the rides it was
  // joined against (#592/#1906), so every rider was filtered out.
  useEffect(() => {
    if (personType !== PERSON_TYPES.RIDER) return undefined;
    // #776 — org context (X-Org-Id) isn't wired into scoreApi until
    // activeOrgId resolves, and the active show isn't known until the show
    // list settles. Fetching before then returns an empty/unscoped list; stay
    // on the skeleton (don't flip loading off) until the context-ready run.
    if (!activeOrgId || showsLoading) {
      setLoading(true);
      return undefined;
    }
    // Context is ready but the org has no show at all: nobody rides anywhere.
    if (!activeShowId) {
      setPeople([]);
      setError(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const list = unwrap(await scoreApi.people.listShowRiders(activeShowId));
        if (!cancelled) setPeople(list);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personType, scoreApi, activeOrgId, activeShowId, showsLoading]);

  // Judges: FVD has no judge "person" record — judges live as names on each
  // class's judgeAssignments ({ position: [judgeName, ...] }). Collect the
  // distinct set across the active show's classes.
  useEffect(() => {
    if (personType !== PERSON_TYPES.JUDGE) return undefined;
    // #776 — stay on the skeleton until show context resolves rather than
    // flipping loading off on a pre-context empty result.
    if (!activeShowId) {
      setLoading(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const classes = unwrap(await scoreApi.classes.listClasses(activeShowId));
        const names = new Set();
        for (const c of classes) {
          for (const list of Object.values(c.judgeAssignments || {})) {
            for (const name of list || []) {
              if (name && !isPlaceholderEntityName(name)) names.add(name);
            }
          }
        }
        if (!cancelled) setJudgeNames([...names].sort((a, b) => a.localeCompare(b)));
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personType, activeShowId, scoreApi]);

  // Scribes: not score-persons at all — account members holding the scribe
  // role for the active org (mirrors ClassManager.jsx's scribe load), sourced
  // from the shared, fully-paged useOrgMembers hook above (#2339).
  useEffect(() => {
    if (personType !== PERSON_TYPES.SCRIBE) return;
    if (scribesError) setError(scribesError);
  }, [personType, scribesError]);

  // #3187 -- scribes are DERIVED from the members (not synced through an
  // effect) and the Scribes tab's skeleton is derived at render below. Both
  // used to lag one commit behind a tab switch: `loading` still held the
  // Riders tab's settled `false` and `scribes` was still [] for the first
  // frame, so "No people found." flashed before the members request started.
  // #776 -- `!activeOrgId` stays on the skeleton until org context resolves.
  const scribes = useMemo(
    () =>
      personType === PERSON_TYPES.SCRIBE
        ? orgMembers.filter((m) => (m.roles || []).includes(SCRIBE_ROLE))
        : [],
    [personType, orgMembers],
  );
  const showLoading = personType === PERSON_TYPES.SCRIBE ? !activeOrgId || scribesLoading : loading;

  // The server already drops owners/trainers/coaches who never ride in the
  // show and FVD placeholder rows ("***Break***"), so this is search only.
  const filteredRiders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) => personName(p).toLowerCase().includes(q));
  }, [people, search]);

  // Card #3610 -- the "Scoresheets go to" column: the staff-set Scoresheet
  // recipients for the active show, else the address(es) FVD sent.
  const recipientsLabel = (person, showId) => {
    const staff = staffRecipientsFor(person, showId);
    const list = staff.length ? staff : splitEmailAddresses(person.email);
    return list.length ? list.join(", ") : "-";
  };
  // A saved list replaces the rider's per-show list in the local rows (and the
  // open drawer's rider) so the table and editor reflect it with no reload.
  const onRecipientsSaved = (riderId, emails) => {
    const apply = (p) => {
      if (p.id !== riderId) return p;
      const byShow = { ...(p.scoresheetRecipients || {}) };
      if (emails.length) byShow[activeShowId] = emails;
      else delete byShow[activeShowId];
      return { ...p, scoresheetRecipients: byShow };
    };
    setPeople((prev) => prev.map(apply));
    setSelected((prev) => (prev ? apply(prev) : prev));
  };

  // #557 — sortable columns over the search-filtered rider rows.
  const { sorted, sortKey, sortDir, toggleSort } = useSortedRows(
    filteredRiders,
    { name: (p) => personName(p), usefId: (p) => p.usefId },
    "name",
  );

  const filteredJudges = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return judgeNames;
    return judgeNames.filter((name) => name.toLowerCase().includes(q));
  }, [judgeNames, search]);

  const filteredScribes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scribes;
    return scribes.filter((m) => userDisplayName(m).toLowerCase().includes(q));
  }, [scribes, search]);

  const isRiderTab = personType === PERSON_TYPES.RIDER;
  const isJudgeTab = personType === PERSON_TYPES.JUDGE;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("people.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("people.loadError")}
        </Alert>
      )}

      <Tabs
        value={personType}
        onChange={(_e, value) => {
          setPersonType(value);
          setSelected(null);
        }}
        sx={{ mb: 2 }}
      >
        {TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={t(tab.labelKey)} />
        ))}
      </Tabs>

      <TextField
        size="small"
        fullWidth
        sx={{ mb: 2, maxWidth: 360 }}
        placeholder={t("people.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {showLoading ? (
        <TableSkeleton rows={5} columns={isRiderTab ? 4 : 1} />
      ) : isRiderTab ? (
        sorted.length === 0 ? (
          <Typography color="text.secondary">{t("people.empty")}</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortKey === "name" ? sortDir : false}>
                    <TableSortLabel
                      active={sortKey === "name"}
                      direction={sortKey === "name" ? sortDir : "asc"}
                      onClick={() => toggleSort("name")}
                    >
                      {t("people.columns.name")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortKey === "usefId" ? sortDir : false}>
                    <TableSortLabel
                      active={sortKey === "usefId"}
                      direction={sortKey === "usefId" ? sortDir : "asc"}
                      onClick={() => toggleSort("usefId")}
                    >
                      {t("people.columns.usefId")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t("people.columns.nextRide")}</TableCell>
                  <TableCell>{t("people.columns.scoresheetsTo")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((person) => (
                  <TableRow
                    key={person.id}
                    hover
                    onClick={() => setSelected(person)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{personName(person)}</TableCell>
                    <TableCell>{person.usefId || "-"}</TableCell>
                    <TableCell>
                      <NextRideCell
                        nextRide={nextByRider[person.id]}
                        ringLabel={t("nextRide.ring")}
                        emptyLabel={t("nextRide.none")}
                      />
                    </TableCell>
                    <TableCell data-testid={`scoresheets-to-${person.id}`}>
                      {recipientsLabel(person, activeShowId)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : isJudgeTab ? (
        filteredJudges.length === 0 ? (
          <Typography color="text.secondary">{t("people.empty")}</Typography>
        ) : (
          <List>
            {filteredJudges.map((name) => (
              <ListItem key={name} divider>
                <ListItemText primary={name} primaryTypographyProps={{ color: "text.primary" }} />
              </ListItem>
            ))}
          </List>
        )
      ) : filteredScribes.length === 0 ? (
        <Typography color="text.secondary">{t("people.empty")}</Typography>
      ) : (
        <List>
          {filteredScribes.map((m) => (
            <ListItem key={m.userId || m.email} divider>
              <ListItemText
                primary={userDisplayName(m)}
                primaryTypographyProps={{ color: "text.primary" }}
              />
            </ListItem>
          ))}
        </List>
      )}

      {isRiderTab && (
        <RideHistoryDrawer
          open={!!selected}
          onClose={() => setSelected(null)}
          title={selected ? personName(selected) : ""}
          infoItems={
            selected
              ? [
                  { label: t("people.columns.usefId"), value: selected.usefId },
                  { label: t("nav.people"), value: selected.personType },
                ]
              : []
          }
          filterKey="riderId"
          subjectId={selected?.id}
        >
          {selected && activeShowId && (
            <ScoresheetRecipientsEditor
              key={selected.id}
              showId={activeShowId}
              rider={selected}
              onSaved={onRecipientsSaved}
            />
          )}
        </RideHistoryDrawer>
      )}
    </Box>
  );
}
